import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BellRing, BellOff, Clock3, RefreshCw, TriangleAlert } from 'lucide-react'
import { useClockRecovery } from '../../hooks/useClockRecovery.js'
import { useClockRuntime } from '../../hooks/useClockRuntime.js'
import { nextLiveState } from '../../lib/clock.js'
import { ClockCanvas } from './ClockCanvas.js'
import { Button } from '../ui/button.js'
import './clock.css'

function readViewerIdentity() {
  const displayId = window.location.pathname.split('/').filter(Boolean)[1] ?? ''
  const key = `trafficplay.clock.${displayId}`
  let fragment = window.location.hash.slice(1)
  try { fragment = decodeURIComponent(fragment) } catch { fragment = '' }
  if (fragment) {
    try { localStorage.setItem(key, fragment) } catch { /* mode kiosque sans stockage persistant */ }
    history.replaceState(null, '', `/clock/${displayId}`)
  }
  let stored: string | null = null
  try { stored = localStorage.getItem(key) } catch { /* stockage indisponible */ }
  return { displayId, token: fragment || stored }
}

export const ClockViewer = memo(function ClockViewer() {
  const [{ displayId, token }] = useState(readViewerIdentity)
  const { bootstrap, sync, error, reload } = useClockRuntime(displayId, token)
  // Écran laissé ouvert en permanence : il se recharge seul après une mise à
  // jour ou une désynchronisation durable, au lieu d'attendre un F5.
  useClockRecovery({ displayId, token, active: Boolean(bootstrap), stale: sync.stale, wsConnected: sync.wsConnected })
  const [soundEnabled, setSoundEnabled] = useState(false)
  const phaseRef = useRef<string>('')
  const audioRef = useRef<AudioContext | null>(null)
  const layout = bootstrap?.template?.config
  const theme = bootstrap?.display.themeOverride ?? bootstrap?.template?.defaultTheme ?? 'DARK'
  const live = useMemo(() => nextLiveState(bootstrap?.items ?? [], sync.nowMs), [bootstrap?.items, sync.nowMs])
  const canSound = bootstrap?.display.displayKind === 'CONTROL_ROOM' && bootstrap.display.audibleAlerts

  const enableSound = useCallback(() => {
    const AudioCtor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtor) return
    audioRef.current ??= new AudioCtor()
    void audioRef.current.resume()
    setSoundEnabled(true)
  }, [])

  useEffect(() => {
    const previous = phaseRef.current
    phaseRef.current = live.phase
    if (!previous || previous === live.phase || !canSound || !soundEnabled || sync.stale) return
    const count = ['IMMINENT', 'ENDING_IMMINENT', 'WAITING', 'OVERRUN'].includes(live.phase) ? 3 : ['SOON', 'ENDING_SOON'].includes(live.phase) ? 1 : 0
    const context = audioRef.current
    if (!context || !count) return
    for (let index = 0; index < count; index += 1) {
      const oscillator = context.createOscillator()
      const gain = context.createGain()
      oscillator.frequency.value = live.severity === 'danger' ? 880 : 660
      gain.gain.value = 0.08
      oscillator.connect(gain).connect(context.destination)
      oscillator.start(context.currentTime + index * 0.16)
      oscillator.stop(context.currentTime + index * 0.16 + 0.08)
    }
  }, [canSound, live.phase, live.severity, soundEnabled, sync.stale])

  useEffect(() => {
    let lock: WakeLockSentinel | null = null
    if ('wakeLock' in navigator) void navigator.wakeLock.request('screen').then(value => { lock = value }).catch(() => undefined)
    return () => { void lock?.release() }
  }, [])
  useEffect(() => {
    const previous = document.documentElement.dataset.theme
    document.documentElement.dataset.theme = theme === 'DARK' ? 'antenne' : 'clair'
    return () => { if (previous) document.documentElement.dataset.theme = previous; else delete document.documentElement.dataset.theme }
  }, [theme])

  if (!token) return <ViewerMessage icon={<BellOff />} title="Lien Clock désactivé" detail="Le secret de cet écran est absent ou a été retiré." />
  if (error && !bootstrap) return <ViewerMessage icon={<TriangleAlert />} title={error} detail="Vérifiez le lien ou demandez sa rotation depuis TrafficPlay." action={<Button onClick={() => void reload()}><RefreshCw />Réessayer</Button>} />
  if (!bootstrap) return <ViewerMessage icon={<Clock3 />} title="Initialisation Clock" detail="Synchronisation de l’horloge et chargement du conducteur…" />
  if (!layout) return <ViewerMessage icon={<TriangleAlert />} title="Aucun modèle publié" detail="Publiez puis affectez un modèle à cet écran." />

  return (
    <main className="clock-viewer" data-theme={theme === 'DARK' ? 'antenne' : 'clair'}>
      {sync.stale && <div className="clock-viewer__stale" role="alert"><TriangleAlert /> DONNÉES NON SYNCHRONISÉES · DERNIÈRE MISE À JOUR IL Y A {sync.dataAgeMs == null ? '—' : `${Math.floor(sync.dataAgeMs / 1000)} S`}</div>}
      <div className="clock-viewer__meta">SYNCHRO {sync.rtt == null ? '—' : `${Math.round(sync.rtt)} ms`}</div>
      {canSound && <Button className="clock-viewer__sound" variant="secondary" onClick={enableSound}>{soundEnabled ? <BellRing /> : <BellOff />}{soundEnabled ? 'SON ACTIF' : 'ACTIVER LE SON'}</Button>}
      <div className="clock-viewer__stage"><ClockCanvas config={layout} channel={bootstrap.channel} items={bootstrap.items} playout={bootstrap.playout} nowMs={sync.nowMs} /></div>
      <div className="sr-only" role="status">{live.label}</div>
    </main>
  )
})

const ViewerMessage = memo(function ViewerMessage({ icon, title, detail, action }: { icon: React.ReactNode; title: string; detail: string; action?: React.ReactNode }) {
  return <main className="clock-viewer clock-viewer--message" data-theme="antenne"><div>{icon}<h1>{title}</h1><p>{detail}</p>{action}</div></main>
})
